import { useState, useEffect, useCallback, useRef } from "react";
import { supabaseAdmin } from "../lib/supabaseAdmin";

import { getEdgeBaseUrl } from "@/lib/tenantConfig";

const EDGE_BASE = getEdgeBaseUrl();

async function adminFetch(action: string, body?: Record<string, unknown>) {
  const {
    data: { session },
  } = await supabaseAdmin.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("未登入");

  const url = `${EDGE_BASE}/admin-data?action=${encodeURIComponent(action)}`;
  const res = await fetch(url, {
    method: body ? "POST" : "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.error || `請求失敗 (${res.status})`);
  }
  return res.json();
}

export function useAdmin() {
  const [overview, setOverview] = useState<{
    totalSubscriptions: number;
    totalProfit: number;
    activeUsers: number;
    pendingWithdrawals: number;
    revenueTrend: { month: string; value: number }[];
  } | null>(null);

  const [products, setProducts] = useState<{
    trial_products: Record<string, unknown>[];
    vip_products: Record<string, unknown>[];
  } | null>(null);

  const [orders, setOrders] = useState<{
    orders: Record<string, unknown>[];
  } | null>(null);

  const [users, setUsers] = useState<{
    users: Record<string, unknown>[];
  } | null>(null);

  const [withdrawals, setWithdrawals] = useState<{
    withdrawals: Record<string, unknown>[];
  } | null>(null);

  const [verifications, setVerifications] = useState<{
    verifications: Record<string, unknown>[];
  } | null>(null);

  const [loading, setLoading] = useState<Record<string, boolean>>();
  const [error, setError] = useState<Record<string, string>>();

  const fetchedRef = useRef<Record<string, boolean>>();

  if (!fetchedRef.current) {
    fetchedRef.current = {};
  }

  const safeSetLoading = useCallback((key: string, value: boolean) => {
    setLoading((p) => ({ ...p, [key]: value }));
  }, []);

  const safeSetError = useCallback((key: string, value: string) => {
    setError((p) => ({ ...p, [key]: value }));
  }, []);

  const fetchOverview = useCallback(async () => {
    if (fetchedRef.current!.overview) return;
    fetchedRef.current!.overview = true;
    safeSetLoading("overview", true);
    safeSetError("overview", "");
    try {
      const data = await adminFetch("overview");
      setOverview(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "資料載入失敗";
      safeSetError("overview", msg);
    } finally {
      safeSetLoading("overview", false);
    }
  }, [safeSetLoading, safeSetError]);

  const fetchProducts = useCallback(async () => {
    if (fetchedRef.current!.products) return;
    fetchedRef.current!.products = true;
    safeSetLoading("products", true);
    safeSetError("products", "");
    try {
      const data = await adminFetch("products");
      setProducts(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "資料載入失敗";
      safeSetError("products", msg);
    } finally {
      safeSetLoading("products", false);
    }
  }, [safeSetLoading, safeSetError]);

  const fetchOrders = useCallback(async () => {
    if (fetchedRef.current!.orders) return;
    fetchedRef.current!.orders = true;
    safeSetLoading("orders", true);
    safeSetError("orders", "");
    try {
      const data = await adminFetch("orders");
      setOrders(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "資料載入失敗";
      safeSetError("orders", msg);
    } finally {
      safeSetLoading("orders", false);
    }
  }, [safeSetLoading, safeSetError]);

  const fetchUsers = useCallback(async () => {
    if (fetchedRef.current!.users) return;
    fetchedRef.current!.users = true;
    safeSetLoading("users", true);
    safeSetError("users", "");
    try {
      const data = await adminFetch("users");
      setUsers(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "資料載入失敗";
      safeSetError("users", msg);
    } finally {
      safeSetLoading("users", false);
    }
  }, [safeSetLoading, safeSetError]);

  const fetchWithdrawals = useCallback(async () => {
    if (fetchedRef.current!.withdrawals) return;
    fetchedRef.current!.withdrawals = true;
    safeSetLoading("withdrawals", true);
    safeSetError("withdrawals", "");
    try {
      const data = await adminFetch("withdrawals");
      setWithdrawals(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "資料載入失敗";
      safeSetError("withdrawals", msg);
    } finally {
      safeSetLoading("withdrawals", false);
    }
  }, [safeSetLoading, safeSetError]);

  const fetchVerifications = useCallback(async () => {
    if (fetchedRef.current!.verifications) return;
    fetchedRef.current!.verifications = true;
    safeSetLoading("verifications", true);
    safeSetError("verifications", "");
    try {
      const data = await adminFetch("verifications");
      setVerifications(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "資料載入失敗";
      safeSetError("verifications", msg);
    } finally {
      safeSetLoading("verifications", false);
    }
  }, [safeSetLoading, safeSetError]);

  const updateWithdrawalStatus = useCallback(
    async (id: string, status: string) => {
      const data = await adminFetch("update_withdrawal", { id, status });
      await fetchWithdrawals();
      await fetchOverview();
      return data;
    },
    [fetchWithdrawals, fetchOverview]
  );

  const updateVerificationStatus = useCallback(
    async (user_id: string, verification_status: string, rejection_reason?: string | null) => {
      const body: Record<string, unknown> = { user_id, verification_status };
      if (rejection_reason !== undefined && rejection_reason !== null) {
        body.rejection_reason = rejection_reason;
      }
      const data = await adminFetch("update_verification", body);
      return data;
    },
    []
  );

  const updateUserLevel = useCallback(
    async (user_id: string, membership_level: string) => {
      const data = await adminFetch("update_user_level", { user_id, membership_level });
      await fetchUsers();
      await fetchOverview();
      return data;
    },
    [fetchUsers, fetchOverview]
  );

  const isAdmin = useCallback(async (): Promise<boolean> => {
    try {
      const data = await adminFetch("overview");
      return !!data;
    } catch {
      return false;
    }
  }, []);

  return {
    overview,
    products,
    orders,
    users,
    withdrawals,
    verifications,
    loading,
    error,
    fetchOverview,
    fetchProducts,
    fetchOrders,
    fetchUsers,
    fetchWithdrawals,
    fetchVerifications,
    updateWithdrawalStatus,
    updateVerificationStatus,
    updateUserLevel,
    isAdmin,
  };
}